/**
 * Console override utility to suppress console statements in production
 */

interface ConsoleOverride {
  log: (...args: any[]) => void;
  warn: (...args: any[]) => void;
  error: (...args: any[]) => void;
  info: (...args: any[]) => void;
  debug: (...args: any[]) => void;
  trace: (...args: any[]) => void;
  group: (...args: any[]) => void;
  groupEnd: (...args: any[]) => void;
  groupCollapsed: (...args: any[]) => void;
  time: (label?: string) => void;
  timeEnd: (label?: string) => void;
  timeLog: (label?: string, ...data: any[]) => void;
  count: (label?: string) => void;
  countReset: (label?: string) => void;
  clear: () => void;
  dir: (obj?: any, options?: any) => void;
  dirxml: (...data: any[]) => void;
  table: (tabularData?: any, properties?: string[]) => void;
  assert: (condition?: boolean, ...data: any[]) => void;
}

/**
 * No-op function that does nothing
 */
const noop = (...args: any[]) => {};

/**
 * Original console methods (stored before override)
 */
let originalConsole: ConsoleOverride | null = null;

/**
 * Override console methods to prevent output in production
 * @param forceOverride - If true, overrides even in development
 */
export const overrideConsole = (forceOverride: boolean = false): void => {
  // Check if we're in production or if force override is enabled
  const isProduction = process.env.NODE_ENV === 'production';
  
  if (!isProduction && !forceOverride) {
    return; // Don't override in development unless forced
  }

  // Store original console methods if not already stored
  if (!originalConsole) {
    originalConsole = {
      log: console.log,
      warn: console.warn,
      error: console.error,
      info: console.info,
      debug: console.debug,
      trace: console.trace,
      group: console.group,
      groupEnd: console.groupEnd,
      groupCollapsed: console.groupCollapsed,
      time: console.time,
      timeEnd: console.timeEnd,
      timeLog: console.timeLog,
      count: console.count,
      countReset: console.countReset,
      clear: console.clear,
      dir: console.dir,
      dirxml: console.dirxml,
      table: console.table,
      assert: console.assert,
    };
  }

  // Override all console methods with no-op
  console.log = noop;
  console.warn = noop;
  console.error = noop;
  console.info = noop;
  console.debug = noop;
  console.trace = noop;
  console.group = noop;
  console.groupEnd = noop;
  console.groupCollapsed = noop;
  console.time = noop;
  console.timeEnd = noop;
  console.timeLog = noop;
  console.count = noop;
  console.countReset = noop;
  console.clear = noop;
  console.dir = noop;
  console.dirxml = noop;
  console.table = noop;
  console.assert = noop;
};

/**
 * Restore original console methods
 */
export const restoreConsole = (): void => {
  if (!originalConsole) {
    return; // Nothing to restore
  }

  // Restore all console methods
  console.log = originalConsole.log;
  console.warn = originalConsole.warn;
  console.error = originalConsole.error;
  console.info = originalConsole.info;
  console.debug = originalConsole.debug;
  console.trace = originalConsole.trace;
  console.group = originalConsole.group;
  console.groupEnd = originalConsole.groupEnd;
  console.groupCollapsed = originalConsole.groupCollapsed;
  console.time = originalConsole.time;
  console.timeEnd = originalConsole.timeEnd;
  console.timeLog = originalConsole.timeLog;
  console.count = originalConsole.count;
  console.countReset = originalConsole.countReset;
  console.clear = originalConsole.clear;
  console.dir = originalConsole.dir;
  console.dirxml = originalConsole.dirxml;
  console.table = originalConsole.table;
  console.assert = originalConsole.assert;

  // Clear the stored original console
  originalConsole = null;
};

/**
 * Check if console is currently overridden
 */
export const isConsoleOverridden = (): boolean => {
  return console.log === noop;
};

/**
 * Conditional console override - only override specific methods
 * @param methods - Array of console methods to override
 * @param forceOverride - If true, overrides even in development
 */
export const overrideSpecificConsoleMethods = (
  methods: (keyof ConsoleOverride)[] = ['log', 'warn', 'info', 'debug'],
  forceOverride: boolean = false
): void => {
  const isProduction = process.env.NODE_ENV === 'production';
  
  if (!isProduction && !forceOverride) {
    return;
  }

  // Store original console methods if not already stored
  if (!originalConsole) {
    originalConsole = {
      log: console.log,
      warn: console.warn,
      error: console.error,
      info: console.info,
      debug: console.debug,
      trace: console.trace,
      group: console.group,
      groupEnd: console.groupEnd,
      groupCollapsed: console.groupCollapsed,
      time: console.time,
      timeEnd: console.timeEnd,
      timeLog: console.timeLog,
      count: console.count,
      countReset: console.countReset,
      clear: console.clear,
      dir: console.dir,
      dirxml: console.dirxml,
      table: console.table,
      assert: console.assert,
    };
  }

  // Override only specified methods
  methods.forEach(method => {
    if (method in console) {
      (console as any)[method] = noop;
    }
  });
};

/**
 * Initialize console override based on environment
 * This function can be called early in the application lifecycle
 */
export const initConsoleOverride = (): void => {
  // Auto-override in production
  if (process.env.NODE_ENV === 'production') {
    overrideConsole();
  } else {
    restoreConsole();
  }
};
